import { action, computed, makeObservable, observable } from 'mobx';
import { ChangeEvent, KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { IPlayerAdapter } from './playerSchema';
import clamp from 'lodash/clamp';

type Props = BindingProps<{
  adapter: IPlayerAdapter
}>

/**
 * Handles events occuring on the volume button and slider and exposes properties
 * for creating the appropriate UX for the button.
 */
export class PlayerVolumeButtonState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable(this, {
      adapter: computed,
      volumeBeforeMute: observable,
      volume: computed,
      isMuted: computed,
      volumeBeforeDrag: observable,
      isDragging: observable,
      volumeBeforeKey: observable,
      isKeying: observable,
      mute: action,
      unmute: action,
      toggleMute: action,
    });
  }

  get adapter(): IPlayerAdapter | null {
    return this.getResolvedProp('adapter');
  }

  volumeBeforeMute: number | null = null;

  get volume() {
    return this.adapter?.volume ?? 0;
  }
  get isMuted() {
    return this.volume === 0;
  }

  volumeBeforeDrag: number | null = null;
  isDragging = false;

  volumeBeforeKey: number | null = null;
  isKeying = false;

  handleKeyDown = action((_evt: KeyboardEvent) => {
    if (this.isKeying)
      // keydown is fired continuously while the key is being pressed
      // if the key is already pressed, ignore the event
      return;

    // notify the state that a key is being held and store the current volume
    this.volumeBeforeKey = this.volume;
    this.isKeying = true;
  })

  handleKeyUp = action((_evt: KeyboardEvent) => {
    this.isKeying = false;
    if (this.isMuted && this.volumeBeforeKey !== 0)
      this.volumeBeforeMute = this.volumeBeforeKey;
    this.volumeBeforeKey = null;
  })

  handleSliderPointerDown = action((_evt: PointerEvent) => {
    if (this.isDragging)
      // for multi-pointer scenarios ignore the second pointer
      return;

    this.volumeBeforeDrag = this.volume;
    this.isDragging = true;
  })

  handleSliderPointerUp = action((_evt: PointerEvent) => {
    this.isDragging = false;
    if (this.isMuted && this.volumeBeforeDrag !== 0)
      this.volumeBeforeMute = this.volumeBeforeDrag;
    this.volumeBeforeDrag = null;
  })

  handleSliderChange = action((evt: ChangeEvent<HTMLInputElement>) => {
    const volume = clamp(parseFloat(evt.target.value), 0, 1) ?? 0;
    if (!this.isDragging) {

    }

    this.adapter?.invoke('setVolume', { volume });
  })

  handleButtonClick = action((_evt: MouseEvent) => {
    if (this.isMuted)
      this.unmute();
    else
      this.mute();
  })

  mute() {
    this.volumeBeforeMute = this.volume;
    this.adapter?.invoke('setVolume', { volume: 0 });
  }

  unmute() {
    this.adapter?.invoke('setVolume', { volume: this.volumeBeforeMute || 0.5 });
    this.volumeBeforeMute = null;
  }

  toggleMute() {

    if (this.isMuted || this.volume === 0)
      this.unmute();
    else
      this.mute();
  }
}